import React, { useCallback, useEffect, useState } from "react";
import { Plus, X, Trash2 } from "lucide-react";
import { crmApi } from "../../lib/crmApi";
import { PageHead, Field, inputCls, SERVICE_CATEGORIES } from "./crmUi";

const EMPTY = {
  name: "", email: "", password: "", whatsapp_number: "",
  services_handled: [], areas_handled: "", languages: "",
  availability_status: "available", priority_level: 1, visual_generate_immediately: false,
};

export default function StaffPage() {
  const [staff, setStaff] = useState(null);
  const [editing, setEditing] = useState(null); // null | {..form}
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    crmApi.staff().then((d) => setStaff(d.staff)).catch((e) => setErr(e.message));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const open = (s) =>
    setEditing(s ? {
      ...s,
      password: "",
      areas_handled: (s.areas_handled || []).join(", "),
      languages: (s.languages || []).join(", "),
      services_handled: s.services_handled || [],
    } : { ...EMPTY });

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        name: editing.name, email: editing.email,
        whatsapp_number: editing.whatsapp_number,
        services_handled: editing.services_handled,
        areas_handled: String(editing.areas_handled).split(",").map((x) => x.trim()).filter(Boolean),
        languages: String(editing.languages).split(",").map((x) => x.trim()).filter(Boolean),
        availability_status: editing.availability_status,
        priority_level: Number(editing.priority_level) || 1,
        visual_generate_immediately: !!editing.visual_generate_immediately,
      };
      if (editing.password) body.password = editing.password;
      if (editing.id) await crmApi.updateStaff(editing.id, body);
      else await crmApi.createStaff({ ...body, password: editing.password });
      setEditing(null);
      reload();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}? Their leads become unassigned.`)) return;
    try { await crmApi.deleteStaff(s.id); reload(); } catch (e) { setErr(e.message); }
  };

  const toggleService = (k) =>
    setEditing((p) => ({
      ...p,
      services_handled: p.services_handled.includes(k)
        ? p.services_handled.filter((x) => x !== k)
        : [...p.services_handled, k],
    }));

  return (
    <div data-testid="crm-staff-page">
      <PageHead eyebrow="Team" title="Sales Staff">
        <button
          type="button"
          onClick={() => open(null)}
          data-testid="crm-staff-new"
          className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#56151F]"
        >
          <Plus size={12} /> Add Staff
        </button>
      </PageHead>
      {err && !editing && <p className="text-xs text-[#A3141A] mb-2">{err}</p>}
      {!staff && <p className="text-xs text-[#7A6B5F]">Loading…</p>}
      {staff && staff.length === 0 && (
        <p className="text-sm text-[#7A6B5F] italic">No sales staff yet — add your first team member. They log in at <code>/crm/login</code>.</p>
      )}
      {staff && staff.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {staff.map((s) => (
            <div key={s.id} data-testid={`crm-staff-card-${s.id}`} className={`bg-white border p-5 ${s.active === false ? "opacity-60 border-[#7A6B5F]/30" : "border-[#6E1F2C]/10"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-serif-display text-lg text-[#1C110C]">{s.name}</p>
                  <p className="text-[11px] text-[#7A6B5F]">{s.email} · {s.whatsapp_number || "no WhatsApp"}</p>
                </div>
                <span className={`text-[9px] tracking-[0.16em] uppercase px-2 py-0.5 border ${
                  s.availability_status === "available" ? "text-[#3F6E2C] border-[#3F6E2C]/40"
                  : s.availability_status === "busy" ? "text-[#A6822F] border-[#A6822F]/40"
                  : "text-[#7A6B5F] border-[#7A6B5F]/40"
                }`}>
                  {s.availability_status || "available"}
                </span>
              </div>
              <p className="text-[11px] text-[#3E2A22] mt-3">
                <strong>{s.current_active_leads}</strong> active lead{s.current_active_leads === 1 ? "" : "s"} · priority {s.priority_level}
              </p>
              <p className="text-[10px] text-[#7A6B5F] mt-1 capitalize">
                {(s.services_handled || []).map((k) => k.replace(/_/g, " ")).join(", ") || "All services"}
              </p>
              <p className="text-[10px] text-[#7A6B5F]">{(s.areas_handled || []).join(", ")}{s.languages?.length ? ` · ${s.languages.join(", ")}` : ""}</p>
              {s.visual_generate_immediately && <p className="text-[9px] text-[#A6822F] uppercase tracking-[0.12em] mt-1">✦ instant concept generation</p>}
              <div className="flex gap-2 mt-4">
                <button type="button" onClick={() => open(s)} data-testid={`crm-staff-edit-${s.id}`} className="text-[9px] tracking-[0.18em] uppercase border border-[#6E1F2C]/40 text-[#6E1F2C] px-3 py-1.5 hover:bg-[#6E1F2C] hover:text-white">Edit</button>
                <button type="button" onClick={() => remove(s)} className="text-[9px] tracking-[0.18em] uppercase border border-[#A3141A]/40 text-[#A3141A] px-2 py-1.5 hover:bg-[#A3141A] hover:text-white"><Trash2 size={11} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#1C110C]/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-[520px] border border-[#6E1F2C]/20 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="crm-staff-modal">
            <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between">
              <h3 className="font-serif-display text-lg text-[#1C110C]">{editing.id ? `Edit — ${editing.name}` : "Add Sales Staff"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="text-[#7A6B5F]"><X size={18} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name"><input data-testid="crm-staff-name" className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
              <Field label="WhatsApp number"><input className={inputCls} value={editing.whatsapp_number} onChange={(e) => setEditing({ ...editing, whatsapp_number: e.target.value })} placeholder="+9715…" /></Field>
              <Field label="Login email"><input data-testid="crm-staff-email" type="email" className={inputCls} value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></Field>
              <Field label={editing.id ? "New password (optional)" : "Password"}>
                <input data-testid="crm-staff-password" type="text" className={inputCls} value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} />
              </Field>
              <Field label="Availability">
                <select className={inputCls} value={editing.availability_status} onChange={(e) => setEditing({ ...editing, availability_status: e.target.value })}>
                  {["available", "busy", "on_leave"].map((a) => <option key={a} value={a}>{a.replace("_", " ")}</option>)}
                </select>
              </Field>
              <Field label="Priority level (1 = first)">
                <input type="number" min="1" className={inputCls} value={editing.priority_level} onChange={(e) => setEditing({ ...editing, priority_level: e.target.value })} />
              </Field>
              <Field label="Areas handled (comma-separated)" className="sm:col-span-2">
                <input className={inputCls} value={editing.areas_handled} onChange={(e) => setEditing({ ...editing, areas_handled: e.target.value })} placeholder="Dubai, Sharjah, Ajman" />
              </Field>
              <Field label="Languages (comma-separated)" className="sm:col-span-2">
                <input className={inputCls} value={editing.languages} onChange={(e) => setEditing({ ...editing, languages: e.target.value })} placeholder="English, Arabic, Hindi" />
              </Field>
              <div className="sm:col-span-2">
                <label className="block text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F] mb-1.5">Services handled</label>
                <div className="flex flex-wrap gap-1.5">
                  {SERVICE_CATEGORIES.map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => toggleService(k)}
                      className={`text-[9px] tracking-[0.12em] uppercase px-2 py-1 border ${
                        editing.services_handled.includes(k)
                          ? "bg-[#6E1F2C] text-white border-[#6E1F2C]"
                          : "border-[#6E1F2C]/25 text-[#6E1F2C]"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer text-xs text-[#3E2A22]">
                  <input type="checkbox" data-testid="crm-staff-instant-generate" className="accent-[#6E1F2C] w-4 h-4"
                    checked={!!editing.visual_generate_immediately}
                    onChange={(e) => setEditing({ ...editing, visual_generate_immediately: e.target.checked })} />
                  Trusted — can generate concept images immediately (skips mandatory prompt review; sending still needs approval)
                </label>
              </div>
              {err && <p className="text-xs text-[#A3141A] sm:col-span-2">{err}</p>}
              <div className="sm:col-span-2 flex justify-end gap-2">
                <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">Cancel</button>
                <button type="button" onClick={save} disabled={busy} data-testid="crm-staff-save" className="bg-[#6E1F2C] text-white px-5 py-2 text-[10px] tracking-[0.2em] uppercase disabled:opacity-60">
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
